// AES-256-GCM for ephemeral bodies — nonce = (epoch || nonce_seq) as 12-byte BE.

const ALGORITHM = 'AES-GCM';
const IV_LENGTH = 12;

/** Build the 12-byte GCM IV: upper 8 = epoch, lower 4 = nonce_seq. */
export function ephemeralNonce(epoch: number, nonceSeq: number): Uint8Array {
  const iv = new Uint8Array(IV_LENGTH);
  const view = new DataView(iv.buffer, iv.byteOffset, iv.byteLength);
  view.setBigUint64(0, BigInt(epoch), false);
  view.setUint32(8, nonceSeq >>> 0, false);
  return iv;
}

async function importAesKey(rawKey: Uint8Array): Promise<CryptoKey> {
  if (rawKey.length !== 32) {
    throw new Error('ephemeral crypto: key must be 32 bytes');
  }
  return crypto.subtle.importKey(
    'raw',
    rawKey as BufferSource,
    { name: ALGORITHM, length: 256 },
    false,
    [
    'encrypt',
    'decrypt',
  ]);
}

export async function encryptEphemeralBody(
  key: Uint8Array,
  epoch: number,
  nonceSeq: number,
  plaintext: Uint8Array,
): Promise<Uint8Array> {
  const cryptoKey = await importAesKey(key);
  const iv = ephemeralNonce(epoch, nonceSeq);
  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: iv as BufferSource },
    cryptoKey,
    plaintext as BufferSource,
  );
  return new Uint8Array(ciphertext);
}

export async function decryptEphemeralBody(
  key: Uint8Array,
  epoch: number,
  nonceSeq: number,
  ciphertext: Uint8Array,
): Promise<Uint8Array> {
  const cryptoKey = await importAesKey(key);
  const iv = ephemeralNonce(epoch, nonceSeq);
  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv: iv as BufferSource },
    cryptoKey,
    ciphertext as BufferSource,
  );
  return new Uint8Array(plaintext);
}

/** Wipe a secret byte array in-place when done (best-effort). */
export function wipeBytes(bytes: Uint8Array): void {
  bytes.fill(0);
}
